import { CircularProgress, Box } from '@mui/material';
import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { usePractice } from '../context/PracticeContext';
import { UserRole } from '../types/user';

interface SubscriptionGuardProps {
  children?: React.ReactNode;
  redirectTo?: string;
}

/**
 * SubscriptionGuard checks if a practice admin user has an active subscription.
 * All status is read from PracticeContext (server-computed) — no async work here.
 */
const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({
  children,
  redirectTo = '/subscription/splash',
}) => {
  const { isActive, isLoading } = usePractice();
  const { currentUser } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // If user is not a practice admin or has an active subscription, allow access
  if (!currentUser || currentUser.role !== UserRole.PracticeAdmin || isActive) {
    return children ? children : <Outlet />;
  }

  // If practice admin without active subscription, redirect to splash page
  return <Navigate to={redirectTo} state={{ from: location }} replace />;
};

export default SubscriptionGuard;
